import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { userApi } from '../services/api';
import { 
  FiUser, 
  FiMail, 
  FiLock, 
  FiEye, 
  FiEyeOff,
  FiEdit2,
  FiCheck,
  FiX,
  FiAlertCircle,
  FiCalendar 
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

function ProfilePage() {
  const { user, updateProfile, changePassword } = useAuthStore();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || ''
      });
      setIsLoading(false);
    }
  }, [user]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validatePasswordChange = () => {
    const newErrors = {};
    
    if (!passwordFormData.currentPassword) {
      newErrors.currentPassword = 'Aktuální heslo je povinné';
    }
    
    if (!passwordFormData.newPassword) {
      newErrors.newPassword = 'Nové heslo je povinné';
    } else if (passwordFormData.newPassword.length < 8) {
      newErrors.newPassword = 'Heslo musí mít alespoň 8 znaků';
    }
    
    if (passwordFormData.newPassword !== passwordFormData.confirmNewPassword) {
      newErrors.confirmNewPassword = 'Hesla se neshodují';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveProfile = async () => {
    try {
      setIsLoading(true);
      
      const changes = {};
      if (formData.firstName !== user.firstName) {
        changes.firstName = formData.firstName;
      }
      if (formData.lastName !== user.lastName) {
        changes.lastName = formData.lastName;
      }
      
      if (Object.keys(changes).length === 0) {
        toast.info('Žádné změny k uložení');
        setIsEditing(false);
        return;
      }
      
      await updateProfile(changes);
      toast.success('Profil úspěšně aktualizován');
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error('Nepodařilo se aktualizovat profil');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePassword = async () => {
    if (!validatePasswordChange()) {
      return;
    }
    
    try {
      setIsChangingPassword(true);
      await changePassword(passwordFormData.currentPassword, passwordFormData.newPassword);
      
      toast.success('Heslo úspěšně změněno');
      setShowPasswordSection(false);
      setPasswordFormData({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      });
      setErrors({});
    } catch (err) {
      console.error('Error changing password:', err);
      const message = err.response?.data?.error?.message || 
                     'Nepodařilo se změnit heslo';
      toast.error(message);
      
      // Check if it's wrong password error
      if (err.response?.data?.error?.code === 'INVALID_CURRENT_PASSWORD') {
        setErrors(prev => ({
          ...prev,
          currentPassword: 'Aktuální heslo je nesprávné'
        }));
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getInitials = () => {
    return (formData.firstName.charAt(0) + formData.lastName.charAt(0)).toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Můj profil</h1>
        <p className="text-sm text-gray-600 mt-1">
          Správa vašeho uživatelského účtu
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-md p-6 text-center">
            <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-white">{getInitials()}</span>
            </div>
            
            <h2 className="text-lg font-semibold text-gray-900">
              {formData.firstName} {formData.lastName}
            </h2>
            <p className="text-gray-500">{formData.email}</p>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                Účet vytvořen
              </div>
              <div className="font-medium text-gray-900">
                {user?.createdAt ? format(new Date(user.createdAt), 'dd.MM.yyyy') : 'Neznámo'}
              </div>
            </div>
            
            {user?.role === 'ADMIN' && (
              <div className="mt-4">
                <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                  Administrátor
                </span>
              </div>
            )}
          </div>

          {/* Quick actions */}
          <div className="bg-white rounded-xl shadow-md p-6 mt-6">
            <h3 className="font-semibold text-gray-900 mb-4">Rychlé akce</h3>
            <div className="space-y-3">
              <button
                onClick={() => setIsEditing(true)}
                className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
              >
                <FiEdit2 className="mr-2" />
                Upravit profil
              </button>
              
              <button
                onClick={() => setShowPasswordSection(!showPasswordSection)}
                className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
              >
                <FiLock className="mr-2" />
                Změnit heslo
              </button>
              
              <button
                onClick={() => {
                  window.location.href = '/reservations';
                }}
                className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 transition-colors"
              >
                <FiCalendar className="mr-2" />
                Moje rezervace
              </button>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <div className="lg:col-span-2">
          {/* Profile Information */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Osobní údaje</h2>
              {isEditing && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      // Reset form data
                      setFormData({
                        firstName: user.firstName || '',
                        lastName: user.lastName || '',
                        email: user.email || ''
                      });
                    }}
                    className="px-3 py-1 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <FiX className="mr-1" /> Zrušit
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    disabled={isLoading}
                    className="flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-lg shadow-sm hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {isLoading ? (
                      <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <FiCheck className="mr-1" />
                    )}
                    Uložit
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jméno</label>
                <div className="relative">
                  <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleProfileChange}
                    disabled={!isEditing}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isEditing ? 'border-gray-300' : 'border-transparent bg-gray-50'
                    }`}
                    placeholder="Jméno"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Příjmení</label>
                <div className="relative">
                  <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleProfileChange}
                    disabled={!isEditing}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isEditing ? 'border-gray-300' : 'border-transparent bg-gray-50'
                    }`}
                    placeholder="Příjmení"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <div className="relative">
                  <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    disabled={true}
                    className="w-full pl-10 pr-3 py-2 border border-transparent bg-gray-50 rounded-lg shadow-sm cursor-not-allowed"
                    placeholder="Email"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Emailovou adresu nelze změnit. Kontaktujte administrátora.
                </p>
              </div>
            </div>
          </div>

          {/* Change Password Section */}
          {showPasswordSection && (
            <div className="bg-white rounded-xl shadow-md p-6 mt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Změnit heslo</h2>
                <button
                  onClick={() => {
                    setShowPasswordSection(false);
                    setPasswordFormData({
                      currentPassword: '',
                      newPassword: '',
                      confirmNewPassword: ''
                    });
                    setErrors({});
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX className="text-xl" />
                </button>
              </div>

              {errors.currentPassword && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm mb-4">
                  <FiAlertCircle className="inline mr-1" />
                  {errors.currentPassword}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Aktuální heslo *
                  </label>
                  <div className="relative">
                    <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="currentPassword"
                      value={passwordFormData.currentPassword}
                      onChange={handlePasswordChange}
                      className={`w-full pl-10 pr-10 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.currentPassword ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Zadejte aktuální heslo"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  {errors.currentPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.currentPassword}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nové heslo *
                    </label>
                    <div className="relative">
                      <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        name="newPassword"
                        value={passwordFormData.newPassword}
                        onChange={handlePasswordChange}
                        className={`w-full pl-10 pr-10 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.newPassword ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Min. 8 znaků"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showNewPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                    {errors.newPassword && (
                      <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Potvrzení hesla *
                    </label>
                    <div className="relative">
                      <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmNewPassword"
                        value={passwordFormData.confirmNewPassword}
                        onChange={handlePasswordChange}
                        className={`w-full pl-10 pr-10 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.confirmNewPassword ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Potvrďte nové heslo"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                    {errors.confirmNewPassword && (
                      <p className="mt-1 text-sm text-red-600">{errors.confirmNewPassword}</p>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg text-sm">
                  <p>
                    <strong>Tip:</strong> Použijte silné heslo obsahující písmena, čísla a znaky.
                  </p>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handleSavePassword}
                    disabled={isChangingPassword}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg shadow-sm hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {isChangingPassword ? (
                      <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <FiCheck className="mr-2" />
                    )}
                    Změnit heslo
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
